import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppRoutingModule } from './app-routing.module';
import { IonicModule, IonicRouteStrategy, NavController } from '@ionic/angular';
import { CoreModule } from './core/core.module';
import { SessionService } from './core/services/session.service';
import { BackendService } from './core/services/backend-prod.service';
import { IonicStorageModule } from '@ionic/storage';

import { Observable, Observer } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppComponent } from './app.component';
import { Router, RouteReuseStrategy } from '@angular/router';
import { APP_BASE_HREF, HashLocationStrategy, LocationStrategy, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { HelperService } from './core/services/helper.service';
import { HTTP_INTERCEPTORS, HttpClientXsrfModule } from '@angular/common/http';
import { HttpInterceptorService } from './core/services/http-interceptor.service';
import { SideNavigationComponent } from './shared/components/side-navigation/side-navigation.component';
import { MenuService } from './core/services/menu.service';
import { WINDOW_PROVIDERS } from './window.provider';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { VendorSideNavigationComponent } from './vendor/shared/components/side-navigation/side-navigation.component';
import { NoRightClickDirective } from './shared/directives/disable-right-click.directive';
import { SafePipe } from './core/pipes/safe.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MarkdownModule, MarkedOptions } from 'ngx-markdown';

import 'prismjs';
import 'prismjs/components/prism-typescript.min.js';
import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
import 'prismjs/plugins/line-highlight/prism-line-highlight.js';
import { ScrollDispatcher } from '@angular/cdk/scrolling';

import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel, EventType } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { environment as prodEnv } from 'src/environments/environment.prod';
import { CoPilotComponent } from './shared/components/co-pilot/co-pilot.component';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CoPilotService } from './core/services/co-pilot.service';
import { MatTooltipModule } from '@angular/material/tooltip';

export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM();
}

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal

export function loggerCallback(logLevel: LogLevel, message: string) {
  // //console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  //console.log('We are getting MSALInstanceFactory instance');
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal_clientId, // B2C AD
      // authority: `https://login.microsoftonline.com/${environment.msal_talentId}`,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: environment.msal_redirectUri,
      postLogoutRedirectUri: environment.msal_redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      allowNativeBroker: false,
      allowRedirectInIframe: true,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  //console.log('We are getting MSALInterceptorConfigFactory instance');
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read'
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  //console.log('We are getting MSALGuardConfigFactory instance');
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
      // prompt: 'login'
      prompt: 'select_account'
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SideNavigationComponent,
    VendorSideNavigationComponent,
    NoRightClickDirective,
    SafePipe,
    CoPilotComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    CoreModule,
    IonicStorageModule.forRoot(),
    IonicModule.forRoot({ animated: true }),
    MatDatepickerModule, // provides moment date adapter
    MatMomentDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MarkdownModule.forRoot({
      // markedOptions: {
      //   provide: MarkedOptions,
      //   useValue: {
      //     gfm: true, // GitHub-flavored markdown
      //     tables: true, // Enable tables
      //     // Add any other options as needed
      //   },
      // },
    }),
    HttpClientXsrfModule.withOptions({
      cookieName:'csrf_access_token',
      headerName:'X-CSRF-Token'
    }),
  ],
  exports: [SafePipe],
  providers: [
    ScrollDispatcher,
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation, SessionService, BackendService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [SessionService, BackendService, Router],
      multi: true
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    HelperService,
    MenuService,
    WINDOW_PROVIDERS,
    // BaseAppAuthGuardService

    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: MomentDateAdapter },
    //   {
    //     provide: DateAdapter,
    //     useClass: MomentDateAdapter,
    //     deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true  // Allows multiple interceptors
    },
  ],
  bootstrap: [AppComponent,MsalRedirectComponent]
})
export class AppModule { }

export function initializeApp(sessionService: SessionService, backendService: BackendService, router: Router, nav: NavController) {


  monitorActivity();
  return () => new Promise((resolve, reject) => {
    if (sessionService.checkUserLoggedIn()) {
      validateUserSession().pipe(tap((data: boolean) => {
        if (!data) {
          sessionService.validSession = false
          sessionService.clearStorage();
        } else {
          sessionService.validSession = true;
          sessionService.updateStorageDetails(data);
        }
      }))
        .subscribe(
          () => resolve(true),
          error => {
            //console.log(error);
          })
    } else {
      resolve(true);
    }
  });


  function validateUserSession() {
    return backendService.Get('validateLoggedInSession');
  }

  // function refreshToken() {
  //   return backendService.GetAPICall('refreshToken', sessionService.token, { rememberMe: sessionService.rememberMe });
  // }
  function refreshToken() {
    let rememberMe = false;
    if(sessionService.rememberMe != null){
      rememberMe = sessionService.rememberMe
    }else{
      rememberMe = false
    }
    console.log({ rememberMe }); // Debugging
    return backendService.GetAPICall('refreshToken', sessionService.token, { rememberMe: rememberMe });
  }

  function monitorActivity() {
    let userActive;
    document.addEventListener('mousemove', () => {
      userActive = true;
    });
    document.addEventListener('keypress', () => {
      userActive = true;
    });
    setInterval(() => logoutRoute(), 2000)
    function logoutRoute() {
      sessionService.getStorageDetails().then((res) => {
        if (window.location.href.includes('start-free-trial') || window.location.href.includes('forgot-password') || window.location.href.includes('verify') || window.location.href.includes('set-password') || window.location.href.includes('reset-password') || window.location.href.includes('emailer-page') || window.location.href.includes('change-password') || window.location.href.includes('vendor')) {
          //token is not requried (Without token pages)
        } else {
          if (sessionService.token == null || sessionService.token == undefined) {
            router.navigate(['login']);
            return
          }
        }
      })
    }
    setInterval(() => resetIdle(), 60000)
    function resetIdle() {
      let expiryDateTime = new Date(`${sessionService.expTime}z`);
      let curentDateTime = new Date();
       //console.log(expiryDateTime, curentDateTime);
      let diffInMin = (expiryDateTime.valueOf() - curentDateTime.valueOf()) / 60000;
      //console.log("Remaining minutes:", diffInMin);
      let diffInMinString = diffInMin.toFixed();
      let fixedDiffInMin = Number(diffInMinString);
      //console.log("fixedDiffInMin", fixedDiffInMin);
      if (fixedDiffInMin < 2) {
        if (userActive) {
          refreshToken().subscribe(
            (data) => {
              console.log("As you are active so session got extended");
              sessionService.updateStorageDetails(data);

            },
            error => {
              //console.log(error);
            })
        } else {
          // nav.navigateRoot('login').then(() => window.location.reload());
          console.log("As you are inactive so log out");
          router.navigate(['login']);
          console.log("timed out");
          sessionService.clearStorage();

        }
      }
      userActive = false;
    }

  }
}
